import { Link } from "react-router-dom";
import { BadgeCheck, Clock, Globe, Mail, MapPin, MessageSquarePlus, Navigation, Phone, X } from "lucide-react";
import { Button } from "../ui/button";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState } from "../common/States";
import { directionsLink, osmLink } from "../../lib/directoryLabels";
import ReportDialog from "./ReportDialog";

const Row = ({ icon: Icon, children, testId }) => (
  <p className="flex items-start gap-2 text-sm text-slate-600" data-testid={testId}><Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /><span className="min-w-0 break-words">{children}</span></p>
);

const Contacts = ({ a }) => (
  <div className="flex flex-wrap gap-2">
    {a.phone && <Button asChild size="sm" variant="outline" data-testid="agency-call"><a href={`tel:${a.phone.replace(/\s/g, "")}`}><Phone className="mr-1.5 h-3.5 w-3.5" />Appeler</a></Button>}
    {a.email && <Button asChild size="sm" variant="outline" data-testid="agency-mail"><a href={`mailto:${a.email}`}><Mail className="mr-1.5 h-3.5 w-3.5" />Écrire</a></Button>}
    {a.has_location && <Button asChild size="sm" variant="outline" data-testid="agency-directions"><a href={directionsLink(a.latitude, a.longitude)} target="_blank" rel="noreferrer"><Navigation className="mr-1.5 h-3.5 w-3.5" />Itinéraire</a></Button>}
    {a.company.website && <Button asChild size="sm" variant="ghost" data-testid="agency-website"><a href={a.company.website} target="_blank" rel="noreferrer"><Globe className="mr-1.5 h-3.5 w-3.5" />Site web</a></Button>}
  </div>
);

export default function AgencySheet({ agencyId, origin, onClose }) {
  const qs = origin ? `?lat=${origin.latitude}&lng=${origin.longitude}` : "";
  const { data: a, loading, error } = useApi(`/public/directory/agencies/${agencyId}${qs}`);
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} />;
  const feedbackUrl = (sid) => `/e/${a.company.slug}?src=annuaire&agence=${a.id}${sid ? `&service=${sid}` : ""}#deposer`;
  return (
    <div className="space-y-5" data-testid="agency-sheet">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/e/${a.company.slug}`} className="eyebrow hover:underline" data-testid="agency-sheet-company">{a.company.name} · {a.company.sector}</Link>
          <h2 className="mt-1 font-display text-xl font-bold tracking-tight" data-testid="agency-sheet-name">{a.name}</h2>
          {a.distance_km != null && <p className="text-xs text-slate-500" data-testid="agency-sheet-distance">à {a.distance_km} km</p>}
        </div>
        {onClose && <Button variant="ghost" size="icon" onClick={onClose} data-testid="agency-sheet-close"><X className="h-4 w-4" /></Button>}
      </div>
      <div className="space-y-2">
        <Row icon={MapPin} testId="agency-sheet-address">{[a.address, a.city, a.country].filter(Boolean).join(", ") || "Adresse non renseignée"}</Row>
        <Row icon={Clock} testId="agency-sheet-hours">{a.hours || "Horaires non renseignés"}</Row>
        {a.phone && <Row icon={Phone} testId="agency-sheet-phone">{a.phone}</Row>}
        {a.email && <Row icon={Mail} testId="agency-sheet-email">{a.email}</Row>}
        {!a.has_contact && <p className="text-xs text-slate-400" data-testid="agency-sheet-no-contact">Aucun contact direct renseigné pour cette agence.</p>}
        <p className="text-xs text-slate-500" data-testid="agency-sheet-location">
          {a.has_location ? <>{a.location_verified && <BadgeCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />}Position {a.location_verified ? "vérifiée" : "déclarée par l'organisation"} · <a className="text-blue-600 hover:underline" href={osmLink(a.latitude, a.longitude)} target="_blank" rel="noreferrer">voir sur OpenStreetMap</a></> : "Position GPS non renseignée"}
        </p>
      </div>
      <Contacts a={a} />
      <div>
        <p className="eyebrow mb-2">Services ({a.services.length})</p>
        {!a.services.length && <p className="text-sm text-slate-500">Aucun service renseigné.</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {a.services.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`agency-service-${s.id}`}>
              <div className="min-w-0"><p className="text-sm font-medium">{s.name}</p>{s.description && <p className="truncate text-xs text-slate-500">{s.description}</p>}</div>
              <Button asChild size="sm" variant="ghost" className="shrink-0 text-blue-700"><Link to={feedbackUrl(s.id)} data-testid={`agency-service-feedback-${s.id}`}>Donner un avis</Link></Button>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
        <Button asChild className="rounded-full" data-testid="agency-feedback-button"><Link to={feedbackUrl()}><MessageSquarePlus className="mr-1.5 h-4 w-4" />Donner mon avis sur cette agence</Link></Button>
        <ReportDialog agency={a} />
      </div>
    </div>
  );
}
